import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import CustomAlert from '@common/components/Feedback/CustomAlert';

/**
 * A section's body as its own dataset stands: its shape while the first answer loads, an alert
 * if it failed, and the last answer dimmed in place while a newer one loads, so a filter change
 * never makes the section jump.
 */
const DatasetState: React.FC<{
  isLoading: boolean;
  hasError: boolean;
  hasData: boolean;
  skeletonHeight?: number;
  skeleton?: React.ReactNode;
  children: React.ReactNode;
}> = ({ isLoading, hasError, hasData, skeletonHeight = 160, skeleton, children }) => {
  const { t } = useTranslation();

  if (hasError && !isLoading) {
    return <CustomAlert header={t('fleet-management-error')} severity="error" />;
  }
  if (!hasData) {
    return skeleton ? (
      <Box aria-busy="true">{skeleton}</Box>
    ) : (
      <Skeleton variant="rounded" height={skeletonHeight} aria-busy="true" />
    );
  }
  return (
    <Box
      aria-busy={isLoading}
      sx={{
        transition: 'opacity 0.2s ease-in-out',
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : undefined,
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
};

export default DatasetState;
